import { Loader2, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

const TONES = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200", amber: "bg-amber-50 text-amber-700 ring-amber-200",
  red: "bg-red-50 text-red-600 ring-red-200", grey: "bg-neutral-100 text-neutral-600 ring-neutral-200",
  orange: "bg-orange-50 text-[#FF5A00] ring-orange-200", navy: "bg-slate-100 text-slate-700 ring-slate-200",
};
const MAP = {
  active: "green", completed: "green", approved: "green", accepted: "green", signed: "green", paid: "green", done: "green", won: "green",
  pass: "green", on_track: "green", received: "green", closed: "green", resolved: "green", connected: "green", success: "green",
  in_progress: "amber", "in progress": "amber", review: "amber", submitted: "amber", issued: "orange", sent: "orange", pending: "amber",
  pending_approval: "amber", partially_paid: "amber", partially_received: "amber", at_risk: "amber", scheduled: "amber", assigned: "amber",
  kickoff: "orange", ordered: "orange", "in construction": "orange", prospect: "navy", open: "orange",
  overdue: "red", rejected: "red", cancelled: "red", lost: "red", fail: "red", delayed: "red", expired: "red", failed: "red",
  urgent: "red", high: "red", medium: "amber", low: "grey", not_configured: "grey", waived: "navy",
};

export const StatusBadge = ({ value, testid }) => {
  if (!value) return null;
  const tone = TONES[MAP[String(value).toLowerCase()] || "grey"];
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset whitespace-nowrap ${tone}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
    </span>
  );
};

export const Loader = ({ label = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center py-16 text-neutral-500" data-testid="loader">
    <Loader2 className="h-8 w-8 animate-spin text-[#FF5A00]" />
    <p className="mt-3 text-sm">{label}</p>
  </div>
);

export const EmptyState = ({ title, text, action, icon: Icon = Inbox }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="empty-state">
    <div className="h-14 w-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-3"><Icon className="h-7 w-7 text-[#FF5A00]" /></div>
    <p className="font-semibold text-[#0D131B]">{title}</p>
    {text && <p className="text-sm text-neutral-500 mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 fade-in">
    <div>
      <h1 className="h1" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Kpi = ({ label, value, hint, to, testid, tone = "text-[#0D131B]", title }) => {
  const body = (
    <div className="card-c p-5 h-full transition-transform duration-200 hover:-translate-y-0.5" title={title}>
      <p className="text-xs font-medium text-neutral-500">{label}</p>
      <p className={`text-2xl font-bold mt-2 ${tone}`} data-testid={testid}>{value}</p>
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
    </div>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
};

export const Bar = ({ value = 0, className = "" }) => (
  <div className={`h-2 rounded-full bg-neutral-100 overflow-hidden ${className}`}>
    <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, value))}%`, background: "var(--grad-primary)" }} />
  </div>
);

export const Card = ({ title, action, children, className = "", testid }) => (
  <div className={`card-c p-5 ${className}`} data-testid={testid}>
    {(title || action) && <div className="flex items-center justify-between mb-4"><h3 className="h3">{title}</h3>{action}</div>}
    {children}
  </div>
);

export const Field = ({ label, value }) => (
  <div><p className="lbl">{label}</p><p className="text-sm text-[#171717] mt-1 break-words">{value ?? "—"}</p></div>
);
